import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown, RadioButton } from 'carbon-components-react';
import { Application20, Carbon20, Switcher20 } from '@carbon/icons-react';
import { Options, OrgTenant } from 'types';
import Utils from 'utilities/helper.util';
import { useComponentMount } from 'utilities/hooks.util';
import {
  setDashboardUIStateRangeText,
  setDashboardUIStateAdminRangeText,
  setDashboardUIStateShowGrid,
  setDashboardUIStateShowPoint,
  setDashboardUIStateShowShape
} from 'redux/dashboard/actions';
import { useDispatch } from 'react-redux';
import { setMapSearchWithExtentZoom } from 'redux/map/actions';
import { selectOrganisationTenants } from 'redux/member/selectors';
import { uniq, orderBy } from 'lodash';

const rangeOptions: Options = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'this-week' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' }
];

const getRangeOptionItem = (value: string) => {
  return rangeOptions.find((o) => value === o.value);
};

const Controls: React.FC<Props> = ({
  showAdminInfo,
  showShapeMap,
  showPointMap,
  showGridMap,
  showTenantSelect,
  tenantId,
  setTenantId,
  setStartDate,
  setEndDate,
  setStartDateAdmin,
  setEndDateAdmin,
  setRangeTextEnquirer,
  setRangeTextAdmin,
  range
}) => {
  const dispatch = useDispatch();
  const orgTenants = useSelector(selectOrganisationTenants);
  const [orgTenantItems, setOrgTenantItems] = useState<Options>([]);
  const handleRangeChange = ({ selectedItem }: any) => {
    if (selectedItem?.value) {
      switch (selectedItem.value) {
        case 'today': {
          const today = Utils.getNewDate();
          const date = Utils.toDateAPIFormat(today);
          showAdminInfo ? setStartDateAdmin(date) : setStartDate(date);
          showAdminInfo ? setEndDateAdmin(date) : setEndDate(date);
          break;
        }
        case 'this-week': {
          const today = Utils.getNewDate().subtract(1, 'day');
          const startOfThisWeek = today.startOf('week').add(1, 'day');
          const endOfThisWeek = today.endOf('week').add(1, 'day');
          showAdminInfo
            ? setStartDateAdmin(Utils.toDateAPIFormat(startOfThisWeek))
            : setStartDate(Utils.toDateAPIFormat(startOfThisWeek));
          showAdminInfo
            ? setEndDateAdmin(Utils.toDateAPIFormat(endOfThisWeek))
            : setEndDate(Utils.toDateAPIFormat(endOfThisWeek));
          break;
        }
        case 'last-7-days': {
          const today = Utils.getNewDate();
          const date = Utils.getDateBeforeDays(6);
          showAdminInfo
            ? setStartDateAdmin(Utils.toDateAPIFormat(date))
            : setStartDate(Utils.toDateAPIFormat(date));
          showAdminInfo
            ? setEndDateAdmin(Utils.toDateAPIFormat(today))
            : setEndDate(Utils.toDateAPIFormat(today));
          break;
        }
        case 'last-30-days': {
          const today = Utils.getNewDate();
          const date = Utils.getDateBeforeDays(29);
          showAdminInfo
            ? setStartDateAdmin(Utils.toDateAPIFormat(date))
            : setStartDate(Utils.toDateAPIFormat(date));
          showAdminInfo
            ? setEndDateAdmin(Utils.toDateAPIFormat(today))
            : setEndDate(Utils.toDateAPIFormat(today));
          break;
        }
        default: {
          break;
        }
      }
      showAdminInfo
        ? setRangeTextAdmin(selectedItem.value)
        : setRangeTextEnquirer(selectedItem.value);
      dispatch(setDashboardUIStateRangeText(selectedItem.value));
      showAdminInfo &&
        dispatch(setDashboardUIStateAdminRangeText(selectedItem.value));
    }
  };

  const getOrgTenantOptionItem = useCallback(
    (value: number) => {
      return orgTenantItems.find((o) => String(value) === o.value);
    },
    [orgTenantItems]
  );

  useEffect(() => {
    if (Array.isArray(orgTenants) && orgTenants.length > 0) {
      const options = orgTenants.map((item: OrgTenant) => {
        const { id, name } = item;

        return {
          value: String(id),
          label: name.replace('DBYD ', '')
        };
      });
      const items = orderBy(uniq(options), ['label'], ['asc']);
      setOrgTenantItems([
        {
          value: '0',
          label: 'National'
        },
        ...items
      ]);
    }
  }, [orgTenants]);

  useComponentMount(() => {
    handleRangeChange({ selectedItem: getRangeOptionItem(range) });
  });

  return (
    <div className="dashboard-component--controls">
      {showTenantSelect && (
        <Dropdown
          ariaLabel="Dashboard Tenant"
          id="dashboard-tenant-dropdown"
          titleText=""
          label=""
          size="sm"
          items={orgTenantItems}
          onChange={(item) =>
            item.selectedItem &&
            setTenantId(
              item.selectedItem.value !== '0'
                ? Number(item.selectedItem.value)
                : undefined
            )
          }
          selectedItem={
            tenantId
              ? getOrgTenantOptionItem(tenantId)
              : {
                  value: '0',
                  label: 'National'
                }
          }
        />
      )}
      <Dropdown
        ariaLabel="Dashboard range"
        id="dashboard-range-download"
        items={rangeOptions}
        label=""
        titleText=""
        size="sm"
        onChange={(evt: any) => {
          dispatch(setMapSearchWithExtentZoom([false]));
          handleRangeChange(evt);
        }}
        selectedItem={getRangeOptionItem(range)}
      />
      {showAdminInfo ? (
        <>
          <div className="controls-radio-container">
            <RadioButton
              id="radio-shape"
              hideLabel
              checked={!showGridMap}
              onClick={() => dispatch(setDashboardUIStateShowGrid(false))}
            />
            <Application20 />
            <label>Shapes</label>
          </div>
          <div className="controls-radio-container">
            <RadioButton
              id="radio-grid"
              hideLabel
              checked={showGridMap}
              onClick={() => dispatch(setDashboardUIStateShowGrid(true))}
            />
            <Carbon20 />
            <label>Grid</label>
          </div>
        </>
      ) : (
        <>
          <div className="controls-radio-container">
            <RadioButton
              aria-label="Shapes toggle"
              checked={showShapeMap}
              id="dashboard-shape-toggle"
              hideLabel
              onClick={() => dispatch(setDashboardUIStateShowShape(true))}
            />
            <Application20 />
            <label>Shapes</label>
          </div>
          <div className="controls-radio-container">
            <RadioButton
              aria-label="Point toggle"
              checked={showPointMap}
              id="dashboard-point-toggle"
              hideLabel
              onClick={() => dispatch(setDashboardUIStateShowPoint(true))}
            />
            <Switcher20 />
            <label>Points</label>
          </div>
        </>
      )}
    </div>
  );
};

type Props = {
  showAdminInfo: boolean;
  showShapeMap: boolean;
  showPointMap: boolean;
  showGridMap: boolean;
  showTenantSelect: boolean;
  tenantId?: number;
  range: string;
  setStartDate: Function;
  setEndDate: Function;
  setStartDateAdmin: Function;
  setEndDateAdmin: Function;
  setRangeTextEnquirer: Function;
  setRangeTextAdmin: Function;
  setTenantId: Function;
};

export default React.memo(Controls);
